import React, { useCallback, useEffect, useState } from "react"
// import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify"
import { useNavigate, useParams } from "react-router-dom"
import { request } from "../../services/request"
import { InvoiceDetail, InvoiceDetails } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import moment from "moment"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { FaChevronLeft } from "react-icons/fa"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"

const Icon: any = FaChevronLeft

const CustomerInvoice: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()

  const [bills, setBills] = useState<InvoiceDetail[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const totalItems = 100
  const [search, setSearch] = useState("")
  const debounceText = useDebounce(search, 500)
  const [sort, setSort] = useState("DESC")
  const [sortCol, setSortCol] = useState("createdAt")
  const param = useParams()
  const navigate = useNavigate()

  const fetchCustomerBills = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))

      const params = {
        page: page,
        limit: rowsPerPage,
        search: debounceText,
        sort: sort,
        sortCol: sortCol,
      }
      const response = await request<InvoiceDetails>({
        url: `/adminCustomers/getInvoice/${param.id}`,
        method: "POST",
        data: params,
      })

      if (response.data) {
        setBills(response.data)
      } else {
        toast.error("Failed to fetch customers: " + response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch, page, rowsPerPage, debounceText, sort, param, sortCol])

  useEffect(() => {
    fetchCustomerBills()
  }, [page, rowsPerPage, debounceText, sort, fetchCustomerBills])

  useEffect(() => {
    if (debounceText) {
      setPage(1) // Reset to first page on search
    }
  }, [debounceText])

  const columns = [
    {
      name: "Invoice Status",
      value: (customer: InvoiceDetail) => customer.invoiceStatus,
      id: "totalInstalled",
    },
    {
      name: "Bill date",
      value: (customer: InvoiceDetail) =>
        moment(Number(customer.invoiceDate)).format("MMM-DD-YYYY hh:mm A"),
      id: "billDate",
    },
    {
      name: "Amount",
      value: (customer: InvoiceDetail) => `${customer.invoiceAmount} ${customer.invoiceCurrency}`,
      id: "type",
    },
    {
      name: "Invoice Pdf",
      value: (customer: InvoiceDetail) => (
        customer?.stripeInvoiceData?.invoice_pdf ?
          <PrimaryButton
            label="Download Pdf"
            className="subscription-status-btn m-0"
            onClick={() => {
              if (customer?.stripeInvoiceData?.invoice_pdf) {
                window.open(customer.stripeInvoiceData.invoice_pdf, "_blank", "noopener,noreferrer");
              }
            }}
          /> : "-"
      ),
      id: "invoice_pdf",
    },
  ]

  const getBack = () => {
    navigate(-1)
  }

  const fetchAllInvoices = async () => {
    try {
      const params = {
        page: 1,
        search: debounceText,
        limit: 100000,
        sort: sort,
        sortCol: sortCol,
      };
      const response = await request<InvoiceDetails>({
        url: `/adminCustomers/getInvoice/${param.id}`,
        method: "POST",
        data: params,
      })
      if (response.data) {
        return response.data;
      } else {
        toast.error("Failed to fetch all Invoices: " + response.message);
        return [];
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`);
      return [];
    }
  };

  const exportCSV = async () => {
    const allData = await fetchAllInvoices();
    if (!allData.length) {
      toast.error("No data available to export.");
      return;
    }
    const headers = [
      "Sr. No.",
      "Invoice Status",
      "Bill date",
      "Amount",
    ];
    const rows = bills.map((bill, index) => [
      index + 1,
      bill.invoiceStatus,
      moment(Number(bill.invoiceDate)).format("MMM-DD-YYYY hh:mm A"),
      `${bill.invoiceAmount} ${bill.invoiceCurrency}`,
    ]);
    donwloadCSV(headers, rows, "Invoices.csv");
  };

  const exportPDF = async () => {
    const allData = await fetchAllInvoices();
    if (!allData.length) {
      toast.error("No data available to export.");
      return;
    }
    const headers = [
      "Sr. No.",
      "Invoice Status",
      "Bill date",
      "Amount",
    ];
    const rows = bills.map((bill, index) => [
      index + 1,
      bill.invoiceStatus,
      moment(Number(bill.invoiceDate)).format("MMM-DD-YYYY hh:mm A"),
      `${bill.invoiceAmount} ${bill.invoiceCurrency}`,
    ]);
    downloadPDF(headers, rows, "Invoices");
  };

  const action: TableAction[] = []
  return (
    <section className=' dashboard-section dashboard-item '>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />

      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='d-flex gap-1 mb-2'>
              <button onClick={getBack} className='d-flex align-items-center'>
                <Icon size={20} />
              </button>
              <div className='dashboard-table-title-section w-100'>
                <h2>Invoice</h2>
                <div className='dashboard-table-title-right-side d-flex gap-2'>
                  <PrimaryButton
                    label='Export CSV'
                    className='max-w-max-content'
                    onClick={exportCSV}
                  />
                  <PrimaryButton
                    label='Export PDF'
                    className='max-w-max-content'
                    onClick={exportPDF}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 mt-4'>
            <div className='table-container-main'>
              <div className='table-details-section'></div>
              <Table
                rowData={bills}
                columns={columns}
                paginate={false}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={totalItems}
                setSortColumn={setSortCol}
                setSortOrder={setSort}
                sortColumn={sortCol}
                sortOrder={sort}
                action={action}
                isSearch={false}
                search={search}
                setSearch={setSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CustomerInvoice
